import {useH5Preview} from 'src/hooks/h5/useH5Preview';
import {useIsKeyPressed} from 'src/hooks/useIsKeyPressed';

import {useTranslations} from './TranslationsContext';

/**
 * Turns the H5 preview on and off for internal users. The control is hidden until Option/Alt is
 * held, so it can't be seen during a customer-facing demo; a banner shows whenever preview is on
 * so nobody mistakes the page for the customer view.
 */
export default function H5PreviewToggle() {
  const {allowed, enabled, setEnabled} = useH5Preview();
  // Gate before mounting the control so its keyboard listener only exists for internal users.
  if (!allowed) {
    return null;
  }
  return <H5PreviewControl enabled={enabled} setEnabled={setEnabled} />;
}

function H5PreviewControl({
  enabled,
  setEnabled,
}: {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
}) {
  const {t} = useTranslations();
  // Call both unconditionally (rules-of-hooks); either Option/Alt key reveals the control.
  const altLeftPressed = useIsKeyPressed('AltLeft');
  const altRightPressed = useIsKeyPressed('AltRight');
  const revealed = altLeftPressed || altRightPressed;

  return (
    <>
      {enabled && (
        <div
          role="status"
          className="fixed top-0 inset-x-0 z-50 bg-amber-500 text-white text-center text-sm font-semibold py-1 px-4 pointer-events-none"
        >
          {t('h5PreviewNotCustomerView')}
        </div>
      )}
      {revealed && (
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className="fixed bottom-4 right-4 z-50 rounded bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold py-2 px-3 shadow-lg"
        >
          {enabled ? t('exitH5Preview') : t('previewH5Sections')}
        </button>
      )}
    </>
  );
}
